/**
 * One shared, rAF-throttled scroll loop for the whole page.
 *
 * Every scroll-driven effect on the site (reveals, parallax, scroll-linked
 * transforms) subscribes here rather than attaching its own listener. That
 * keeps us at a single `scroll` handler and a single layout read per frame no
 * matter how many animated elements are on screen.
 */

/** @type {Set<() => void>} */
const subscribers = new Set();

let frame = 0;
let listening = false;

function flush() {
  frame = 0;
  subscribers.forEach((fn) => fn());
}

function schedule() {
  if (!frame) frame = window.requestAnimationFrame(flush);
}

function start() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });
}

/**
 * Run `fn` on every scroll/resize frame. `fn` is also called once immediately
 * so an effect can settle at its correct position before the first scroll.
 *
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
export function onScroll(fn) {
  if (typeof window === 'undefined') return () => {};

  start();
  subscribers.add(fn);
  fn();

  return () => {
    subscribers.delete(fn);
  };
}

/** Force a pass on the next frame — used after layout-changing interactions. */
export function refreshScrollEffects() {
  if (typeof window !== 'undefined') schedule();
}

export default onScroll;
